import { AlertCircle } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';
import { useTemplateFieldsQuery } from '@/services/api/generated/templatesV2Service';
import { useCreateDocumentMutation } from '@/services/api/lettersService';
import type { DocumentTemplate } from '@/services/types/letters-types';
import {
  buildCoverageChoiceMap,
  COVERAGE_CHOICE_FIELDS,
  DocumentFieldMapping,
} from '@/services/types/letters-types';

import { LoRFieldRenderer } from './LoRFieldRenderer';
import { FieldLabel } from './shared/FieldLabel';
import type { LoRFormData, LoRScalarChoice, LoRTemplateField } from './types';

interface CoverageLoRFormProps {
  matterId: string;
  coverageId: string;
  template: DocumentTemplate;
  importantFields: Set<DocumentFieldMapping>;
  fieldSections: Record<string, any>;
  onSuccess: () => void;
}

const COLOR_STYLES = {
  blue: {
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    border: 'border-gray-200',
    headerBg: 'bg-white',
    errorBorder: 'border-red-200',
    errorHeaderBg: 'bg-red-50',
  },
  green: {
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    border: 'border-gray-200',
    headerBg: 'bg-white',
    errorBorder: 'border-red-200',
    errorHeaderBg: 'bg-red-50',
  },
  orange: {
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
    border: 'border-gray-200',
    headerBg: 'bg-white',
    errorBorder: 'border-red-200',
    errorHeaderBg: 'bg-red-50',
  },
} as const;

const FormSection = ({
  section,
  fields,
  control,
  errors,
  sectionFields,
  formValues,
  importantFields,
}: {
  section: any;
  fields: any[];
  control: any;
  errors: any;
  sectionFields: any[];
  formValues: any;
  importantFields: Set<DocumentFieldMapping>;
}) => {
  const Icon = section.icon;
  const hasErrors = sectionFields.some(
    (field) => errors[field.external_reference],
  );
  const styles = COLOR_STYLES[section.color as keyof typeof COLOR_STYLES];

  return (
    <Card
      className={`${hasErrors ? styles.errorBorder : styles.border} transition-all duration-200`}
    >
      <CardHeader
        className={`${hasErrors ? styles.errorHeaderBg : styles.headerBg} pb-3`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-lg ${styles.iconBg}`}
          >
            <Icon className={`h-4 w-4 ${styles.iconColor}`} />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
              {section.title}
              {hasErrors && (
                <span className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-3 w-3" />
                  <span className="font-normal text-xs">Needs attention</span>
                </span>
              )}
            </CardTitle>
          </div>
          {hasErrors && (
            <div className="text-xs text-red-600 font-medium">
              {
                sectionFields.filter(
                  (field) => errors[field.external_reference],
                ).length
              }{' '}
              error
              {sectionFields.filter((field) => errors[field.external_reference])
                .length !== 1
                ? 's'
                : ''}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {sectionFields.map((field) => {
            const fieldName = field.external_reference as keyof LoRFormData;
            const fieldError = errors[fieldName]?.message;
            const isImportant = importantFields.has(field.mapping);
            const fieldValue = formValues?.[fieldName];
            const isEmpty =
              !fieldValue ||
              (typeof fieldValue === 'string' && fieldValue.trim() === '');
            const shouldHighlight = isImportant && isEmpty;
            const isFullWidth = [
              DocumentFieldMapping.CARRIER_ADDRESS_1,
              DocumentFieldMapping.CARRIER_ADDRESS_2,
              DocumentFieldMapping.INSURED_NAME,
              DocumentFieldMapping.ACCIDENT_LOCATION,
            ].includes(field.mapping);

            return (
              <div
                key={field.id}
                className={`space-y-3 ${isFullWidth ? 'sm:col-span-1 md:col-span-2 lg:col-span-3' : ''} ${
                  shouldHighlight
                    ? 'p-3 bg-red-50 border border-red-100 rounded-lg'
                    : ''
                }`}
              >
                <FieldLabel htmlFor={field.external_reference} required={false}>
                  {field.display_name || field.mapping}
                </FieldLabel>
                <LoRFieldRenderer
                  field={field}
                  control={control}
                  required={false}
                  error={fieldError}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Helper to extract string value from form field data
const extractFieldValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle LoRScalarChoice objects - extract the value property
  if (typeof value === 'object' && value.value !== undefined) {
    return String(value.value);
  }

  // Handle arrays (shouldn't happen in LoR but just in case)
  if (Array.isArray(value)) {
    return value.map(extractFieldValue).join(', ');
  }

  // Handle primitive values
  return String(value);
};

const validateAdjusterContact = (data: LoRFormData): boolean => {
  const contactVal = data[DocumentFieldMapping.ADJUSTER_CONTACT];
  const adjVal = data[DocumentFieldMapping.ADJUSTER_NAME];

  if (!contactVal || !adjVal) return true;

  if (typeof contactVal === 'object' && typeof adjVal === 'object') {
    if (contactVal.context?.adjuster_id && adjVal.context?.adjuster_id) {
      return contactVal.context.adjuster_id === adjVal.context.adjuster_id;
    }
  }
  return true;
};

export const CoverageLoRForm = ({
  matterId,
  coverageId,
  template,
  importantFields,
  fieldSections,
  onSuccess,
}: CoverageLoRFormProps) => {
  const [createDocument, { isLoading: isCreating }] =
    useCreateDocumentMutation();

  // Fetch v2 template fields and coverage data to build form fields + defaults.
  // Previously used useCoverageLoRFields (lor-context endpoint).
  const {
    data: rawTemplateFields,
    isLoading: isTemplateFieldsLoading,
    error: templateFieldsError,
  } = useTemplateFieldsQuery({ id: template.id, matterId, coverageId });

  const {
    data: coverageData,
    isLoading: isCoverageLoading,
    error: coverageError,
  } = useGetCoverageQuery({ matterId, coverageId });

  const isFieldsLoading = isTemplateFieldsLoading || isCoverageLoading;
  const fieldsError = templateFieldsError || coverageError;

  // Build choice arrays from coverage data for dropdown fields
  const choiceMap = useMemo(
    () => buildCoverageChoiceMap(coverageData, coverageId),
    [coverageData, coverageId],
  );

  // Build LoRTemplateField[] from v2 template fields.
  // reference is the canonical mapping key (snake_case DocumentFieldMapping);
  // context_key is the Matter Context key (camelCase) — a different namespace.
  const fields: LoRTemplateField[] = useMemo(() => {
    if (!rawTemplateFields) return [];
    const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
    return v2Fields
      .filter((f) => f.source !== 'SIGNING')
      .map((f) => {
        const mapping = f.reference as DocumentFieldMapping;
        // For choice fields, override default_value with the choice array.
        // The ScalarChoice[] from buildCoverageChoiceMap is structurally
        // compatible with LoRScalarChoice[] (both have value + context).
        const choiceArray = choiceMap[mapping] as LoRScalarChoice[] | undefined;
        const defaultValue = choiceArray ?? f.default_value ?? null;
        return {
          id: f.id,
          display_name: f.display_name ?? null,
          default_value: defaultValue,
          reference: f.reference,
          field_type: f.field_type,
          required: f.required,
          template_id: f.template_id,
          context_key: f.context_key ?? null,
          source: f.source,
          mapping,
          external_reference: mapping,
          type: f.field_type,
          is_required: f.required,
          document_template: f.template_id,
        };
      });
  }, [rawTemplateFields, choiceMap]);

  // Build default form values from coverage data + scalar template field defaults
  const defaultValues: Partial<LoRFormData> = useMemo(() => {
    // Fields whose defaults come from coverage data (dropdown selections), not template default_value
    const coverageDerivedFields = COVERAGE_CHOICE_FIELDS;

    const defaults: Partial<LoRFormData> = {};

    // 1. Scalar template field defaults (carrier name, addresses, dates, etc.)
    if (rawTemplateFields) {
      const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
      v2Fields
        .filter((f) => f.source !== 'SIGNING')
        .forEach((f) => {
          const mapping = f.reference as DocumentFieldMapping;
          if (coverageDerivedFields.has(mapping)) return;
          if (mapping in defaults) return; // already set
          if (f.default_value && typeof f.default_value === 'string' && f.default_value.trim()) {
            // Safe: coverage-derived (LoRScalarChoice) fields are excluded above;
            // remaining scalar fields all accept string values.
            (defaults as Record<string, unknown>)[mapping] = f.default_value;
          }
        });
    }

    // 2. Coverage-derived dropdown defaults — reuse choiceMap so context is consistent
    // with the dropdown options (e.g. adjuster_id, coverage_id, contact_handle).
    COVERAGE_CHOICE_FIELDS.forEach((mapping) => {
      const choices = choiceMap[mapping];
      if (choices && choices.length > 0) {
        const [firstChoice] = choices;
        (defaults as Record<string, unknown>)[mapping] = firstChoice;
      }
    });

    return defaults;
  }, [rawTemplateFields, choiceMap]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<LoRFormData>({
    defaultValues,
  });

  // Watch all form values
  const formValues = watch();

  // Reset form when defaults change
  useEffect(() => {
    if (defaultValues) {
      reset(Object.keys(defaultValues).length > 0 ? defaultValues : {});
    }
  }, [defaultValues, reset]);

  const handleFormSubmit = async (data: LoRFormData) => {
    if (!validateAdjusterContact(data)) {
      toast.error('Adjuster contact does not match selected adjuster.');
      return;
    }

    try {
      const mergeFields: Record<string, string> = {};
      Object.entries(data).forEach(([key, value]) => {
        // Extract clean string values for all fields
        const cleanValue = extractFieldValue(value);
        if (cleanValue.trim() !== '') {
          mergeFields[key] = cleanValue;
        }
      });

      // Address normalization (LoR page only):
      // If Carrier Address 1 contains a full address and Address 2 is empty,
      // split into two lines using a light client-side parse; fall back to simple comma split.
      const addr1Key = DocumentFieldMapping.CARRIER_ADDRESS_1;
      const addr2Key = DocumentFieldMapping.CARRIER_ADDRESS_2;
      const addr1 = mergeFields[addr1Key];
      const addr2 = mergeFields[addr2Key];

      if (addr1 && (!addr2 || addr2.trim() === '')) {
        const [line1, line2] = splitAddressClient(addr1);
        if (line1) mergeFields[addr1Key] = line1;
        if (line2) mergeFields[addr2Key] = line2;
      }

      // Mirror coverage id inside merge_fields for backend audit context
      if (coverageId) {
        mergeFields.coverage_id = coverageId;
      }

      // Special handling for CONTACT_METHOD - extract handle_type from selected adjuster contact
      const adjusterContact = data[DocumentFieldMapping.ADJUSTER_CONTACT];
      if (
        adjusterContact &&
        typeof adjusterContact === 'object' &&
        adjusterContact.context?.contact_handle?.handle_type
      ) {
        mergeFields[DocumentFieldMapping.CONTACT_METHOD] =
          adjusterContact.context.contact_handle.handle_type;
      }

      await createDocument({
        matterId,
        data: {
          document_template_id: template.id,
          coverage_id: coverageId,
          merge_fields: mergeFields,
        },
      }).unwrap();

      toast.success('Letter of Representation created successfully');
      onSuccess();
    } catch (err) {
      console.error('Failed to create document:', err);
      toast.error('Failed to create letter. Please try again.');
    }
  };

  if (isFieldsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600 mx-auto" />
          <div className="space-y-1">
            <p className="text-gray-900 font-medium text-sm">
              Loading form fields...
            </p>
            <p className="text-xs text-gray-500">
              Retrieving coverage data and building your form
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (fieldsError) {
    return (
      <Alert variant="destructive" className="border-red-200 bg-red-50">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-1">
            <p className="font-medium">Unable to load form fields</p>
            <p className="text-sm">
              Please check your connection and try again.
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (fields.length === 0) {
    return (
      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700">
          <p className="font-medium">No form fields available</p>
          <p className="text-sm">
            This template doesn&apos;t have any configured fields.
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <form id="lor-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-6">
          {Object.entries(fieldSections).map(([key, section]) => {
            const sectionFields = fields.filter(
              (field) =>
                section.fields.includes(field.mapping) &&
                importantFields.has(field.mapping),
            );

            if (sectionFields.length === 0) return null;

            return (
              <FormSection
                key={key}
                section={section}
                fields={fields}
                control={control}
                errors={errors}
                sectionFields={sectionFields}
                formValues={formValues}
                importantFields={importantFields}
              />
            );
          })}
        </div>
      </form>

      {/* Footer Actions */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {Object.values(errors).length > 0 && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium text-sm">
                  {Object.values(errors).length} field
                  {Object.values(errors).length !== 1 ? 's' : ''} have errors
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onSuccess}
              disabled={isCreating}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="lor-form"
              disabled={isCreating}
              className="min-w-[140px] bg-blue-600 hover:bg-blue-700"
            >
              {isCreating ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Creating...
                </div>
              ) : (
                'Create Letter'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Simple client-side address splitter tailored for LoR use
const splitAddressClient = (value: string): [string, string] => {
  if (!value) return ['', ''];
  const v = value.trim();
  // Prefer split by comma: first part = line1, remainder = line2
  const parts = v
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return ['', ''];
  if (parts.length === 1) return [parts[0], ''];
  const line1 = parts[0];
  const line2 = parts.slice(1).join(', ');
  return [line1, line2];
};
